import { useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Compass, CheckCircle, Warning, Clock, ArrowRight, Lightning, Globe, MapPin
} from "@phosphor-icons/react";

/**
 * Pathways — A dedicated home for non-grant routes to grant funding.
 *
 * Today this houses Fiscal Sponsorship options + the 1023-EZ self-file path.
 * It pulls live entries tagged category="Fiscal Sponsorship" from the grants
 * collection so the data stays evergreen as we add more sponsors.
 */

const STATIC_PROS_CONS = {
  sgf: {
    name: "Social Good Fund (SGF)",
    badge: "LOWEST BARRIER · NO DEPOSIT",
    badgeTone: "emerald",
    geography: "National (all 50 states)",
    fee: "$29/mo admin (refundable) + 6.5–8% of funds raised",
    url: "https://www.socialgoodfund.org/fiscal-sponsorship/sponsorship-rates/",
    pros: [
      "NO opening deposit (vs PPF's $5,000)",
      "NO minimum balance requirement",
      "Faith-friendly projects accepted",
      "Lowest cash barrier of any verified sponsor — start TODAY",
      "Use as a bridge while filing your own 1023-EZ in parallel",
      "Established California-based sponsor with national reach",
    ],
    cons: [
      "6.5–8% admin fee on grants raised during sponsorship",
      "30–40% of grantmakers won't fund fiscally-sponsored projects (mostly federal)",
      "Onboarding takes 2–4 weeks (slower than PPF's 10 days)",
      "Less SC-funder name recognition than CCF",
    ],
    best_for: "Founders without $5K cash on hand who need to start NOW. The right starting point if budget is tight.",
  },
  ppf: {
    name: "Players Philanthropy Fund (PPF)",
    badge: "FAST · ~10 days",
    badgeTone: "emerald",
    geography: "National (all 50 states)",
    fee: "5–8% of funds raised + $5,000 opening deposit",
    url: "https://ppf.org",
    pros: [
      "Approval in 5–10 business days (vs 3–6 months for your own 501(c)(3))",
      "Apply for foundation grants within 2 weeks of submitting",
      "Unlocks ~60–70% of the grant universe immediately",
      "They handle IRS reporting, tax receipts, and compliance",
      "Donors get tax-deductible status via PPF's 501(c)(3)",
      "National coverage — no SC residency restriction",
    ],
    cons: [
      "Requires $5,000 opening deposit + $30K minimum annual budget projection",
      "5–8% fee off every grant raised + one-time setup fee",
      "Funds go to PPF first, then they disburse to you (less control)",
      "Oversight on spending — major decisions need PPF approval",
      "30–40% of grantmakers won't fund fiscally-sponsored projects (mostly federal)",
    ],
    best_for: "Founders who have $5K cash on hand AND need maximum speed (10 days to start applying).",
  },
  ccf: {
    name: "Coastal Community Foundation",
    badge: "SC-NATIVE · 4–8 weeks",
    badgeTone: "amber",
    geography: "South Carolina (Lowcountry-favored)",
    fee: "5–8% of funds raised + oversight requirements",
    url: "https://coastalcommunityfoundation.org/special-initiatives/fiscal-sponsorships/",
    pros: [
      "Deep credibility with SC funders — opens doors national sponsors can't",
      "Direct relationships with BCBS SC, Boeing, Self Family Foundation, etc.",
      "Coaching + capacity-building support included for sponsored projects",
      "Strong reputation for governance — auditors trust CCF-sponsored projects",
      "SC roots = built-in funder familiarity for SC-based applicants",
    ],
    cons: [
      "Slower onboarding: 4–8 weeks typical review",
      "More selective: capacity-limited, they decline projects that don't fit their focus",
      "Mission alignment required (education, youth, racial equity, basic needs)",
      "Fees apply (similar 5–8% range) plus oversight requirements",
      "Lowcountry-favored — Upstate or Pee Dee projects face additional scrutiny",
      "Less startup-friendly than national alternatives if your project is brand new",
    ],
    best_for: "SC projects with established roots and clear local impact — particularly Lowcountry-based mentorship, education, or community development.",
  },
  cma: {
    name: "Christian Ministry Alliance (FSO)",
    badge: "FAITH-BASED · BY APPLICATION",
    badgeTone: "amber",
    geography: "National (faith-aligned projects)",
    fee: "Custom — disclosed during discovery call",
    url: "https://fso.christianministryalliance.org",
    pros: [
      "Explicit faith-friendly framing — strongest cultural fit for faith-driven founders",
      "\"Just for You\" approach: customized to your culture and vision",
      "Custom giving portal and admin support included",
      "Decades of nonprofit operations experience",
      "Relationship-based onboarding — high-touch from day one",
    ],
    cons: [
      "Pricing NOT published publicly — requires a discovery call to learn fees and minimums",
      "Timeline unclear — approval requires CMA Executive Team review",
      "Less transparent up-front than Social Good Fund",
      "Best fit only for explicitly faith-aligned missions",
      "Schedule a call at fso.christianministryalliance.org/call to start",
    ],
    best_for: "Faith-driven founders who value relationship + cultural alignment over price transparency. Worth a discovery call alongside SGF to compare.",
  },
};

const Badge = ({ tone, children }) => {
  const map = {
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  };
  return (
    <span className={`inline-block px-2.5 py-1 rounded-sm text-xs font-bold tracking-widest uppercase border ${map[tone] || map.emerald}`}>
      {children}
    </span>
  );
};

const ProsConsCard = ({ data, testid }) => (
  <Card className="bg-zinc-950 border-zinc-800" data-testid={testid}>
    <CardContent className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight" data-testid={`${testid}-name`}>{data.name}</h2>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-zinc-500">
            <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{data.geography}</span>
            <span>·</span>
            <span>Fee: {data.fee}</span>
          </div>
        </div>
        <Badge tone={data.badgeTone}>{data.badge}</Badge>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mt-5">
        <div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-emerald-400 mb-3 flex items-center gap-1.5">
            <CheckCircle weight="fill" className="w-4 h-4" /> Pros
          </h3>
          <ul className="space-y-2">
            {data.pros.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-300 leading-snug">
                <CheckCircle weight="fill" className="w-3.5 h-3.5 mt-1 text-emerald-400/80 flex-shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-amber-300 mb-3 flex items-center gap-1.5">
            <Warning weight="fill" className="w-4 h-4" /> Cons
          </h3>
          <ul className="space-y-2">
            {data.cons.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-300 leading-snug">
                <Warning weight="fill" className="w-3.5 h-3.5 mt-1 text-amber-400/80 flex-shrink-0" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-5 pt-5 border-t border-zinc-800/60">
        <p className="text-sm italic text-zinc-300 leading-snug mb-3">
          <span className="text-emerald-400 font-semibold not-italic">Best for:</span> {data.best_for}
        </p>
        <a
          href={data.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-sm bg-emerald-500 text-black text-sm font-bold uppercase tracking-widest hover:bg-emerald-400 transition-colors"
          data-testid={`${testid}-apply`}
        >
          Visit {data.name.split("(")[0].trim()} <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </CardContent>
  </Card>
);

const Pathways = () => {
  const { user, login } = useAuth();

  useEffect(() => {
    // Ping the API so any errors are surfaced in console (helps debug if grants endpoint changes)
    axios.get(`${API}/grants?search=Fiscal+Sponsorship&limit=1`).catch(() => {});
  }, []);

  const body = (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="pathways-page">
        {/* Hero */}
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-sm bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-bold uppercase tracking-widest mb-3" data-testid="pathways-eyebrow">
            <Compass className="w-3.5 h-3.5" />
            Pathways
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3" data-testid="pathways-title">
            Don't have 501(c)(3) yet? <span className="text-emerald-400">There's still a path.</span>
          </h1>
          <p className="text-zinc-400 text-sm sm:text-base leading-relaxed max-w-3xl" data-testid="pathways-subtitle">
            Most grants require 501(c)(3) status. But you don't have to wait 3–6 months for IRS approval to start applying.
            These are the verified fast tracks — sponsors that "house" your project under their 501(c)(3) so you can apply for grants TODAY — plus the in-app 1023-EZ walkthrough so you can file your own 501(c)(3) in parallel.
          </p>
        </div>

        {/* Strategy summary */}
        <Card className="bg-emerald-500/5 border-emerald-500/30" data-testid="pathways-strategy">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Lightning weight="fill" className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-sm uppercase tracking-widest text-emerald-300 mb-2">Recommended Combo</h3>
                <p className="text-sm text-zinc-300 leading-relaxed mb-2">
                  File your own <strong>1023-EZ</strong> ($275, ~3 months) AND apply to a fiscal sponsor in parallel.
                  The sponsor bridges the 90-day gap so you can apply for foundation money NOW.
                  When your own 501(c)(3) comes through, you graduate from the sponsor.
                </p>
                <p className="text-xs text-zinc-500">
                  Eligible for 1023-EZ: annual budget under $50K, projected revenue under $50K, no foreign activities.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fiscal Sponsorship Options */}
        <div>
          <h2 className="text-xl font-bold tracking-tight mb-1">Fiscal Sponsorship Options</h2>
          <p className="text-xs text-zinc-500 mb-4">
            Apply to a sponsor first → they "house" your project under their 501(c)(3) → you immediately become eligible for grants that accept fiscally-sponsored applicants.
          </p>
          <div className="space-y-4">
            <ProsConsCard data={STATIC_PROS_CONS.sgf} testid="pathway-sgf" />
            <ProsConsCard data={STATIC_PROS_CONS.ppf} testid="pathway-ppf" />
            <ProsConsCard data={STATIC_PROS_CONS.ccf} testid="pathway-ccf" />
            <ProsConsCard data={STATIC_PROS_CONS.cma} testid="pathway-cma" />
          </div>
        </div>

        {/* 1023-EZ DIY path */}
        <Card className="bg-zinc-950 border-zinc-800" data-testid="pathways-diy-card">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-xl font-bold tracking-tight">File Your Own 501(c)(3) — IRS Form 1023-EZ</h2>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-zinc-500">
                  <span className="inline-flex items-center gap-1"><Globe className="w-3.5 h-3.5" />U.S. nationwide</span>
                  <span>·</span>
                  <span>$275 filing fee · ~3 months processing</span>
                </div>
              </div>
              <Badge tone="emerald">DIY · ~3 MONTHS</Badge>
            </div>

            <div className="grid sm:grid-cols-2 gap-5 mt-5">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-emerald-400 mb-3 flex items-center gap-1.5">
                  <CheckCircle weight="fill" className="w-4 h-4" /> Pros
                </h3>
                <ul className="space-y-2 text-sm text-zinc-300">
                  <li className="flex items-start gap-2"><CheckCircle weight="fill" className="w-3.5 h-3.5 mt-1 text-emerald-400/80 flex-shrink-0" /><span>You own your 501(c)(3) outright — no fees ever, no oversight</span></li>
                  <li className="flex items-start gap-2"><CheckCircle weight="fill" className="w-3.5 h-3.5 mt-1 text-emerald-400/80 flex-shrink-0" /><span>Unlocks 100% of grant universe (vs 60–70% with fiscal sponsor)</span></li>
                  <li className="flex items-start gap-2"><CheckCircle weight="fill" className="w-3.5 h-3.5 mt-1 text-emerald-400/80 flex-shrink-0" /><span>Only $275 + ~4–6 hours of paperwork</span></li>
                  <li className="flex items-start gap-2"><CheckCircle weight="fill" className="w-3.5 h-3.5 mt-1 text-emerald-400/80 flex-shrink-0" /><span>Donors get tax-deductible status directly</span></li>
                  <li className="flex items-start gap-2"><CheckCircle weight="fill" className="w-3.5 h-3.5 mt-1 text-emerald-400/80 flex-shrink-0" /><span>Federal/government grants become accessible</span></li>
                </ul>
              </div>
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-amber-300 mb-3 flex items-center gap-1.5">
                  <Warning weight="fill" className="w-4 h-4" /> Cons
                </h3>
                <ul className="space-y-2 text-sm text-zinc-300">
                  <li className="flex items-start gap-2"><Warning weight="fill" className="w-3.5 h-3.5 mt-1 text-amber-400/80 flex-shrink-0" /><span>3–6 month wait for IRS approval — can't apply for grants until approved</span></li>
                  <li className="flex items-start gap-2"><Warning weight="fill" className="w-3.5 h-3.5 mt-1 text-amber-400/80 flex-shrink-0" /><span>Must qualify: under $50K annual budget, U.S.-only, no foreign work</span></li>
                  <li className="flex items-start gap-2"><Warning weight="fill" className="w-3.5 h-3.5 mt-1 text-amber-400/80 flex-shrink-0" /><span>Annual 990-N filing required forever (5 minutes, but mandatory)</span></li>
                  <li className="flex items-start gap-2"><Warning weight="fill" className="w-3.5 h-3.5 mt-1 text-amber-400/80 flex-shrink-0" /><span>You handle all compliance, reporting, board minutes, audits</span></li>
                </ul>
              </div>
            </div>

            <div className="mt-5 pt-5 border-t border-zinc-800/60">
              <p className="text-sm italic text-zinc-300 leading-snug mb-3">
                <span className="text-emerald-400 font-semibold not-italic">Best for:</span> Founders building a long-term organization who want full control and access to every funding source. Pair it with fiscal sponsorship for the first 3 months.
              </p>
              <a
                href="/formation/1023-ez"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-sm bg-emerald-500 text-black text-sm font-bold uppercase tracking-widest hover:bg-emerald-400 transition-colors"
                data-testid="pathway-1023ez-apply"
              >
                Start 1023-EZ Walkthrough <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="https://www.irs.gov/forms-pubs/about-form-1023-ez"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 ml-2 rounded-sm border border-zinc-700 text-zinc-300 text-xs font-bold uppercase tracking-widest hover:border-zinc-600 hover:text-zinc-100 transition-colors"
                data-testid="pathway-1023ez-irs-link"
              >
                IRS Form 1023-EZ page
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Compliance note */}
        <p className="text-xs text-zinc-600 text-center max-w-2xl mx-auto">
          <Clock className="w-3 h-3 inline mr-1" />
          FlashGrants does not provide legal or tax advice. Pros and cons are general guidance.
          Verify all fees, terms, and eligibility directly with each sponsor before applying.
        </p>

        {/* Public CTA — only for logged-out visitors */}
        {!user && (
          <Card className="bg-emerald-500/5 border-emerald-500/30" data-testid="pathways-public-cta">
            <CardContent className="p-6 text-center">
              <h3 className="text-xl font-bold tracking-tight mb-2">
                Want the matching grants — picked for your project?
              </h3>
              <p className="text-sm text-zinc-300 mb-5 max-w-2xl mx-auto leading-relaxed">
                FlashGrants matches you to 100+ verified South Carolina grants and walks you through the IRS 1023-EZ filing in-app. Free to start, no credit card.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Button
                  onClick={login}
                  className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold uppercase tracking-widest text-sm"
                  data-testid="pathways-public-google-btn"
                >
                  Continue with Google
                </Button>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-sm border border-zinc-700 text-zinc-300 text-sm font-bold uppercase tracking-widest hover:border-zinc-600 hover:text-zinc-100 transition-colors"
                  data-testid="pathways-public-home-btn"
                >
                  See full grant list
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
  );

  // Logged-in users see the standard dashboard chrome; public visitors see a
  // minimal marketing header so the page works as an SEO landing page too.
  if (user) {
    return <DashboardLayout>{body}</DashboardLayout>;
  }

  return (
    <div className="min-h-screen bg-black text-zinc-100">
      <header className="sticky top-0 z-30 border-b border-zinc-800/60 bg-zinc-950/80 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="pathways-public-logo">
            <Lightning weight="fill" className="w-6 h-6 text-emerald-500" />
            <span className="text-lg font-bold tracking-tight">FlashGrants</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="text-zinc-400 hover:text-white transition-colors text-sm hidden sm:inline"
              data-testid="pathways-public-features-link"
            >
              Features
            </Link>
            <Button
              onClick={login}
              className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-sm h-9"
              data-testid="pathways-public-header-cta"
            >
              Get started — free
            </Button>
          </div>
        </div>
      </header>
      <main>{body}</main>
    </div>
  );
};

export default Pathways;
